import React from "react";
import { Container, Row, Col, Whisper, Tooltip, Tag } from "rsuite";
import PropTypes from "prop-types";
import PromotionSummary from "../../../components/promotion-summary/PromotionSummary";
import cx from "classnames";
import BulkUpdate from "../../bulk-update/bulkUpdate";
import CalendarGuide from "../../calendar-guide/calendarGuide";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { isNegotiatedRateplan } from "../../../slices/rateplanSlice";
import { useIsPilotHotel } from "../../../contexts/PropertyStoreContext";
import { P, usePermission } from "../../../rbac";
dayjs.extend(utc);
const BulkUpdateRightPanel = ({
  rateplan,
  promos,
  derivedRates = [],
  className = "",
  isOffline,
  startDate,
  endDate,
}) => {
  const { allowed: canManageAri } = usePermission(P.Ari.Manage);
  const isEditable =
    canManageAri && (isOffline || isNegotiatedRateplan(rateplan));
  const isPilotHotel = useIsPilotHotel();
  // Pilot properties render the stacked promotions list, whose height varies
  // with content — the guide must flow after it instead of being pinned at
  // the fixed offset the legacy layout uses.
  const calendarGuideClassName =
    isEditable || isPilotHotel ? "" : "with-bulk-update";
  // filter the promos which are applicable for the selected month
  const applicablePromos = promos.filter((promo) => {
    if (!promo.ruleJson) return true;
    try {
      const rule = JSON.parse(promo.ruleJson);
      const stayVO = rule?.ruleVO?.stayVO;
      if (!stayVO) return true;
      const promoStart = dayjs.utc(stayVO.stayStartDate).startOf("day");
      const promoEnd = dayjs.utc(stayVO.stayEndDate).endOf("day");
      const selectedMonth = dayjs.utc(startDate);
      const monthStart = selectedMonth.startOf("month");
      const monthEnd = selectedMonth.endOf("month");
      // Only include if promo covers at least one day in the month
      return (
        (promoStart.isBefore(monthEnd) || promoStart.isSame(monthEnd, "day")) &&
        (promoEnd.isAfter(monthStart) || promoEnd.isSame(monthStart, "day"))
      );
    } catch {
      return true;
    }
  });
  const applicableDerivedRates = derivedRates.filter((dr) => {
    const stayDates = dr.restrictionConfig?.stayDates || [];
    if (stayDates.length === 0) return true;
    const monthStart = dayjs.utc(startDate).startOf("month");
    const monthEnd = dayjs.utc(startDate).endOf("month");
    return stayDates.some(({ startDate: sd, endDate: ed }) => {
      const drStart = dayjs.utc(sd).startOf("day");
      const drEnd = dayjs.utc(ed).endOf("day");
      return (
        (drStart.isBefore(monthEnd) || drStart.isSame(monthEnd, "day")) &&
        (drEnd.isAfter(monthStart) || drEnd.isSame(monthStart, "day"))
      );
    });
  });
  return (
    <Container className={cx("right-panel-container", className)}>
      {!isOffline && rateplan?.data?.additionalData?.isNegotiated && (
        <Row className="static-rates-pill-row">
          <Tag className="static-rates-pill" size="sm">
            Static Rates Enabled
          </Tag>
        </Row>
      )}
      <Row className="right-panel-header-one">
        <Col className={cx("header-rateplan-name", "bulk-header")}>
          <Whisper
            trigger="hover"
            placement="topStart"
            speaker={<Tooltip>{rateplan?.data?.name}</Tooltip>} // Label has code-name
          >
            <span>{rateplan?.data?.name}</span>
          </Whisper>
        </Col>
      </Row>
      <Row className="right-panel-header-two">
        <Col className="header-rateplan-name">
          <span>{rateplan?.data?.code}</span>
        </Col>
      </Row>
      <PromotionSummary
        promos={applicablePromos}
        rateplan={rateplan}
        derivedRates={applicableDerivedRates}
      />
      {isEditable ? (
        <BulkUpdate
          rateplan={rateplan}
          startDate={startDate}
          endDate={endDate}
        />
      ) : null}
      <CalendarGuide className={calendarGuideClassName} />
    </Container>
  );
};

BulkUpdateRightPanel.propTypes = {
  rateplan: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.number,
    data: PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
      additionalData: PropTypes.shape({
        isNegotiated: PropTypes.bool,
      }),
    }),
  }),
  promos: PropTypes.array,
  className: PropTypes.string,
  isOffline: PropTypes.bool,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
};

export default BulkUpdateRightPanel;
